<template>
    <div id="org_settings_profile">
        <div class="card">
            <div class="card-body">
                <form @submit.prevent="updateAgency" enctype="multipart/form-data">
                    <div class="d-md-flex d-block">
                        <div class="flex-shrink-1 profile-image px-2 pe-4">
                            <!-- Logo Preview -->
                            <div class="profile-image-container mb-3">
                                <img 
                                    :src="form.logo || '/img/images/profile_image_placeholder.png'" 
                                    :alt="form.name || 'Agency'"
                                    :key="form.logo || 'placeholder'"
                                    class="profile-image"
                                >
                                <button 
                                    type="button"
                                    class="btn btn-secondary btn-icon-only-sm"
                                    @click="openImageModal"
                                    title="Change Agency Logo"
                                >
                                   <img src="/img/icons/avid-icons/upload.svg" alt="Change Agency Logo" class="icon">
                                </button>
                            </div>
                            <div class="text-danger invalid-feedback" v-if="errors.image !== ''">
                                {{errors.image}}
                            </div>
                        </div>
                        <div class="mt-md-0 mt-3 px-2 w-100">
                            <h3>Profile</h3>
                            <div class="row">
                                <div class="col-12">
                                    <!-- Name -->
                                    <div class="mb-3">
                                        <label class="form-label">Agency Name</label>
                                        <input 
                                            type="text"
                                            class="form-control"
                                            :class="{ 'is-invalid': errors.name }"
                                            v-model="form.name"
                                            @blur="validateName"
                                         aria-label="Agency Name">
                                        <div class="invalid-feedback" v-if="errors.name">
                                            {{ errors.name }}
                                        </div>
                                    </div>
                                    <!-- URL Slug -->
                                    <div class="mb-3">
                                        <label class="form-label">Custom Registration URL 
                                            <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip"
                                                title="You will receive a 20% referral bonus for any nonprofit that signs up for Avid via your custom registration link." alt=""
                                            ></img>
                                        </label>
                                        <div class="input-group mb-3">
                                            <span class="input-group-text">
                                                https://app.avidai.com/register/
                                            </span>
                                            <input 
                                                type="text"
                                                class="form-control"
                                                :class="{ 'is-invalid': errors.url_slug }"
                                                v-model="form.url_slug"
                                                @blur="validateUrlSlug"
                                             aria-label="Text input">
                                            <button 
                                                type="button"
                                                class="btn btn-secondary"
                                                :disabled="!form.url_slug"
                                                @click="copyRegistrationUrl"
                                                aria-label="Copy Registration URL"
                                            >
                                                <i class="fas fa-copy"></i>
                                            </button>
                                        </div>
                                        <div class="invalid-feedback" v-if="errors.url_slug">
                                            {{ errors.url_slug }}
                                        </div>
                                    </div>
                                    <!-- Domain -->
                                    <div class="row">
                                        <div class="col-lg-6 col-12">
                                            <div class="mb-3">
                                                <label class="form-label">Website Domain</label>
                                                <div class="input-group mb-3">
                                                    <span class="input-group-text">
                                                        https://
                                                    </span>
                                                    <input 
                                                        type="text"
                                                        class="form-control"
                                                        :class="{ 'is-invalid': errors.domain }"
                                                        v-model="form.domain"
                                                        @blur="validateDomain"
                                                     aria-label="Text input">
                                                </div>
                                                <div class="invalid-feedback" v-if="errors.domain">
                                                    {{ errors.domain }}
                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-xl-3 col-lg-6 col-12">
                                            <label class="form-label">Text Color
                                                <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip"
                                                    title="Pick a dark color used in titles and other fonts that will be used when generating slides. It should match your branding." alt=""
                                                ></img>
                                            </label>
                                            <div class="input-group mb-3">
                                                <input 
                                                    type="color"
                                                    class="form-control form-control-color"
                                                    v-model="form.dark_color"
                                                    :style="{ backgroundColor: form.dark_color, border: 'none' }"
                                                 aria-label="Color picker">
                                                <input 
                                                    type="text"
                                                    class="form-control"
                                                    v-model="form.dark_color"
                                                 aria-label="Text input">
                                            </div>
                                        </div>
                                        <div class="col-xl-3 col-lg-6 col-12">
                                            <label class="form-label">Accent Color
                                                <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip"
                                                    title="Pick a bright color that will be used as an accent when generating slides. It should match your branding." alt=""
                                                ></img>
                                            </label>
                                            <div class="input-group mb-3">
                                                <input 
                                                    type="color"
                                                    class="form-control form-control-color"
                                                    v-model="form.light_color"
                                                    :style="{ backgroundColor: form.light_color, border: 'none' }"
                                                 aria-label="Color picker">
                                                <input 
                                                    type="text"
                                                    class="form-control"
                                                    v-model="form.light_color"
                                                 aria-label="Text input">
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Chart Colors for Export -->
                                    <div class="mb-3">
                                        <h4 class="mb-1">Chart Colors for Exports 
                                            <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" data-placement="top" title="Customize the colors used in PowerPoint exports to match your brand or preferences." alt=""></img>
                                        </h4>
                                        <div class="row">
                                            <div class="col-xl-3 col-lg-6 col-12">
                                                <label class="form-label">Revenue
                                                    <span class="text-muted small ms-2">(<a href="#" @click.prevent="resetChartColor('revenue')" class="text-decoration-none">reset</a>)</span>
                                                </label>
                                                <div class="input-group mb-3">
                                                    <input 
                                                        type="color"
                                                        class="form-control form-control-color"
                                                        v-model="chartColorHexInputs.revenue"
                                                        :style="{ 'background-color': chartColorHexInputs.revenue }"
                                                        @change="updateChartColorFromHex('revenue', $event.target.value)"
                                                     aria-label="Color picker">
                                                    <input 
                                                        type="text"
                                                        class="form-control"
                                                        v-model="chartColorHexInputs.revenue"
                                                        placeholder="#98CB2E"
                                                        @change="updateChartColorFromHex('revenue', $event.target.value)"
                                                     aria-label="Text input">
                                                </div>
                                            </div>
                                            <div class="col-xl-3 col-lg-6 col-12">
                                                <label class="form-label">Donors
                                                    <span class="text-muted small ms-2">(<a href="#" @click.prevent="resetChartColor('donors')" class="text-decoration-none">reset</a>)</span>
                                                </label>
                                                <div class="input-group mb-3">
                                                    <input 
                                                        type="color"
                                                        class="form-control form-control-color"
                                                        v-model="chartColorHexInputs.donors"
                                                        :style="{ 'background-color': chartColorHexInputs.donors }"
                                                        @change="updateChartColorFromHex('donors', $event.target.value)"
                                                     aria-label="Color picker">
                                                    <input 
                                                        type="text"
                                                        class="form-control"
                                                        v-model="chartColorHexInputs.donors"
                                                        placeholder="#5042FF"
                                                        @change="updateChartColorFromHex('donors', $event.target.value)"
                                                     aria-label="Text input">
                                                </div>
                                            </div>
                                            <div class="col-xl-3 col-lg-6 col-12">
                                                <label class="form-label">Subscribers
                                                    <span class="text-muted small ms-2">(<a href="#" @click.prevent="resetChartColor('subscribers')" class="text-decoration-none">reset</a>)</span>
                                                </label>
                                                <div class="input-group mb-3">
                                                    <input 
                                                        type="color"
                                                        class="form-control form-control-color"
                                                        v-model="chartColorHexInputs.subscribers"
                                                        :style="{ 'background-color': chartColorHexInputs.subscribers }"
                                                        @change="updateChartColorFromHex('subscribers', $event.target.value)"
                                                     aria-label="Color picker">
                                                    <input 
                                                        type="text"
                                                        class="form-control"
                                                        v-model="chartColorHexInputs.subscribers"
                                                        placeholder="#FAAE00"
                                                        @change="updateChartColorFromHex('subscribers', $event.target.value)"
                                                     aria-label="Text input">
                                                </div>
                                            </div>
                                            <div class="col-xl-3 col-lg-6 col-12">
                                                <label class="form-label">Percentages
                                                    <span class="text-muted small ms-2">(<a href="#" @click.prevent="resetChartColor('percentages')" class="text-decoration-none">reset</a>)</span>
                                                </label>
                                                <div class="input-group mb-3">
                                                    <input 
                                                        type="color"
                                                        class="form-control form-control-color"
                                                        v-model="chartColorHexInputs.percentages"
                                                        :style="{ 'background-color': chartColorHexInputs.percentages }"
                                                        @change="updateChartColorFromHex('percentages', $event.target.value)"
                                                     aria-label="Color picker">
                                                    <input 
                                                        type="text"
                                                        class="form-control"
                                                        v-model="chartColorHexInputs.percentages"
                                                        placeholder="#FF1951"
                                                        @change="updateChartColorFromHex('percentages', $event.target.value)"
                                                     aria-label="Text input">
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Admin-only billing fields -->
                    <div v-if="user && user.user_type === 'admin'" class="mt-4">
                        <h4 class="mb-3">Billing Settings</h4>
                        <div class="row">
                            <div class="col-lg-4 col-md-6 col-12 mb-3">
                                <label class="form-label">Cost Per Trial</label>
                                <div class="input-group">
                                    <span class="input-group-text">$</span>
                                    <input 
                                        type="number"
                                        step="0.01"
                                        class="form-control"
                                        :class="{ 'is-invalid': errors.cost_per_trial }"
                                        v-model="form.cost_per_trial"
                                        @blur="validateCostPerTrial"
                                     aria-label="Number">
                                </div>
                                <div class="invalid-feedback" v-if="errors.cost_per_trial">
                                    {{ errors.cost_per_trial }}
                                </div>
                            </div>
                            <div class="col-lg-4 col-md-6 col-12 mb-3">
                                <label class="form-label">Referral Fee Percentage</label>
                                <div class="input-group">
                                    <input 
                                        type="number"
                                        step="1"
                                        min="0"
                                        max="100"
                                        class="form-control"
                                        :class="{ 'is-invalid': errors.referral_percentage }"
                                        v-model="referralPercentageDisplay"
                                        @blur="validateReferralPercentage"
                                     aria-label="Referral Fee Percentage">
                                    <span class="input-group-text">%</span>
                                </div>
                                <div class="invalid-feedback" v-if="errors.referral_percentage">
                                    {{ errors.referral_percentage }}
                                </div>
                            </div>
                            <div class="col-lg-4 col-md-6 col-12 mb-3">
                                <label class="form-label">Payment Terms</label>
                                <select 
                                    class="form-select"
                                    :class="{ 'is-invalid': errors.payment_terms }"
                                    v-model="form.payment_terms"
                                    @blur="validatePaymentTerms"
                                 aria-label="Payment Terms">
                                    <option value="immediate">Immediate</option>
                                    <option value="net_15">Net 15</option>
                                    <option value="net_30">Net 30</option>
                                    <option value="net_60">Net 60</option>
                                    <option value="net_90">Net 90</option>
                                </select>
                                <div class="invalid-feedback" v-if="errors.payment_terms">
                                    {{ errors.payment_terms }}
                                </div>
                            </div>
                            <div class="col-lg-4 col-md-6 col-12 mb-3">
                                <label class="form-label">Client Discount Type</label>
                                <select 
                                    class="form-select"
                                    :class="{ 'is-invalid': errors.cost_per_client_discount_type }"
                                    v-model="form.cost_per_client_discount_type"
                                    @blur="validateDiscountType"
                                 aria-label="Client Discount Type">
                                    <option value="percentage">Percentage</option>
                                    <option value="flat_rate">Flat Rate</option>
                                    <option value="none">None</option>
                                </select>
                                <div class="invalid-feedback" v-if="errors.cost_per_client_discount_type">
                                    {{ errors.cost_per_client_discount_type }}
                                </div>
                            </div>
                            <div v-if="form.cost_per_client_discount_type !== 'none'" class="col-lg-4 col-md-6 col-12 mb-3">
                                <label class="form-label">
                                    {{ form.cost_per_client_discount_type === 'flat_rate' ? 'Client Instance Rate' : 'Client Discount Amount' }}
                                </label>
                                <div class="input-group">
                                    <span v-if="form.cost_per_client_discount_type === 'flat_rate'" class="input-group-text">$</span>
                                    <input 
                                        v-if="form.cost_per_client_discount_type === 'percentage'"
                                        type="number"
                                        step="1"
                                        min="0"
                                        max="100"
                                        class="form-control"
                                        :class="{ 'is-invalid': errors.cost_per_client_discount_amount }"
                                        v-model="discountAmountDisplay"
                                        @blur="validateDiscountAmount"
                                     aria-label="Number">
                                    <input 
                                        v-else
                                        type="number"
                                        step="0.01"
                                        class="form-control"
                                        :class="{ 'is-invalid': errors.cost_per_client_discount_amount }"
                                        v-model="form.cost_per_client_discount_amount"
                                        @blur="validateDiscountAmount"
                                     aria-label="Number">
                                    <span v-if="form.cost_per_client_discount_type === 'percentage'" class="input-group-text">%</span>
                                </div>
                                <div class="invalid-feedback" v-if="errors.cost_per_client_discount_amount">
                                    {{ errors.cost_per_client_discount_amount }}
                                </div>
                            </div>
                            
                            <div :class="form.cost_per_client_discount_type === 'none' ? 'col-lg-6 col-md-6 col-12 mb-3' : 'col-lg-4 col-md-6 col-12 mb-3'">
                                <label class="form-label">Payment Method</label>
                                <select 
                                    class="form-select"
                                    :class="{ 'is-invalid': errors.payment_method }"
                                    v-model="form.payment_method"
                                    @blur="validatePaymentMethod"
                                 aria-label="Payment Method">
                                    <option value="stripe">Stripe</option>
                                    <option value="ach">ACH Push Payment</option>
                                    <option value="bill.com">Bill.com</option>
                                    <option value="other">Other</option>
                                </select>
                                <div class="invalid-feedback" v-if="errors.payment_method">
                                    {{ errors.payment_method }}
                                </div>
                            </div>
                        </div>
                        
                        <div class="row">
                            <div :class="form.cost_per_client_discount_type === 'none' ? 'col-lg-6 col-md-6 col-12 mb-3' : 'col-lg-4 col-md-6 col-12 mb-3'">
                                <div class="form-check mt-4">
                                    <input 
                                        class="form-check-input"
                                        type="checkbox"
                                        :class="{ 'is-invalid': errors.resale_certificate }"
                                        v-model="form.resale_certificate"
                                        id="resale_certificate"
                                     aria-label="Checkbox">
                                    <label class="form-check-label" for="resale_certificate">
                                        Resale Certificate Submitted
                                    </label>
                                    <div class="invalid-feedback" v-if="errors.resale_certificate">
                                        {{ errors.resale_certificate }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="alert alert-danger mt-3" v-if="form.error">
                        There is an error in the fields above. Please correct and try again.
                    </div>
                    <button 
                        type="submit"
                        class="btn btn-primary mt-3 px-4"
                        :disabled="form.busy || hasError()"
                    >
                        <img class="icon me-1" src="/img/icons/avid-icons/save.svg" alt=""> Save
                    </button>
                </form>                
            </div>
        </div>

        <!-- Image Edit Modal -->
        <image-edit-modal
            :id="'image-edit-modal'"
            :current-image="form.logo"
            @image-updated="handleImageUpdate"
            ref="imageEditModal"
        />
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import ImageDropzone from '../components/ImageDropzone.vue';
import ImageEditModal from '../components/ImageEditModal.vue';
import DatepickerUtc from '../components/DatepickerUtc.vue';
import Swal from 'sweetalert2';
import { useNotifications } from '../../composables/useNotifications';

export default defineComponent({
    name: 'AgencyProfile',
    components: {
        ImageDropzone,
        ImageEditModal,
        DatepickerUtc,
        Swal
    },
    setup() {
        const { showNotification } = useNotifications();
        return {
            showNotification
        };
    },
    props: {
        agency: {
            type: Object,
            required: true
        },
        user: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            form: {
                id: this.agency?.id || -1,
                name: this.agency?.name || '',
                logo: this.agency?.logo || '',
                domain: this.agency?.domain || '',
                url_slug: this.agency?.url_slug || '',
                light_color: this.agency?.light_color || '#EFEFEF',
                dark_color: this.agency?.dark_color || '#000000',
                chart_colors: this.agency?.chart_colors || null,
                // Billing fields
                cost_per_trial: this.agency?.cost_per_trial || 2500.00,
                cost_per_client_discount_type: this.agency?.cost_per_client_discount_type || 'none',
                cost_per_client_discount_amount: this.agency?.cost_per_client_discount_amount || 0,
                referral_percentage: this.agency?.referral_percentage || 0,
                payment_terms: this.agency?.payment_terms || 'immediate',
                resale_certificate: this.agency?.resale_certificate || false,
                payment_method: this.agency?.payment_method || 'stripe',
                busy: false,
                error: false,
            },
            errors: {
                name: '',
                domain: '',
                url_slug: '',
                // Billing field errors
                cost_per_trial: '',
                cost_per_client_discount_type: '',
                cost_per_client_discount_amount: '',
                referral_percentage: '',
                payment_terms: '',
                resale_certificate: '',
                payment_method: '',
            },
            chartColorInputs: {
                revenue: '',
                donors: '',
                subscribers: '',
                percentages: ''
            },
            chartColorHexInputs: {
                revenue: '#98CB2E',
                donors: '#5042FF',
                subscribers: '#FAAE00',
                percentages: '#FF1951'
            }
        };
    },
    computed: {
        // Display referral percentage as percentage (10 instead of 0.1)
        referralPercentageDisplay: {
            get() {
                return this.form.referral_percentage ? Math.round(this.form.referral_percentage * 100) : '';
            },
            set(value) {
                this.form.referral_percentage = value ? parseFloat(value) / 100 : 0;
            }
        },
        // Display discount amount as percentage when type is percentage
        discountAmountDisplay: {
            get() {
                if (this.form.cost_per_client_discount_type === 'percentage') {
                    return this.form.cost_per_client_discount_amount ? Math.round(this.form.cost_per_client_discount_amount * 100) : '';
                }
                return this.form.cost_per_client_discount_amount;
            },
            set(value) {
                if (this.form.cost_per_client_discount_type === 'percentage') {
                    this.form.cost_per_client_discount_amount = value ? parseFloat(value) / 100 : 0;
                } else {
                    this.form.cost_per_client_discount_amount = value ? parseFloat(value) : 0;
                }
            }
        }
    },
    mounted() {
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));

        for (var i = 0; i < tooltipTriggerList.length; i++) {
            (function(index) {
            tooltipTriggerList[index].addEventListener('click', function() {
                // Hide the tooltip using the index passed to the closure
                tooltipList[index].hide();
            });
            tooltipTriggerList[index].addEventListener('mouseleave', function() {
                // Hide the tooltip using the index passed to the closure
                tooltipList[index].hide();
            });
            })(i);
        }

        // Default hex colors
        const defaultHexColors = {
            revenue: '#98CB2E',
            donors: '#5042FF',
            subscribers: '#FAAE00',
            percentages: '#FF1951'
        };

        // Initialize hex inputs with existing values or defaults
        // If colors are stored in hex, use them; if in RGB, convert to hex
        this.chartColorHexInputs.revenue = this.getHexFromColor(this.form.chart_colors?.revenue) || defaultHexColors.revenue;
        this.chartColorHexInputs.donors = this.getHexFromColor(this.form.chart_colors?.donors) || defaultHexColors.donors;
        this.chartColorHexInputs.subscribers = this.getHexFromColor(this.form.chart_colors?.subscribers) || defaultHexColors.subscribers;
        this.chartColorHexInputs.percentages = this.getHexFromColor(this.form.chart_colors?.percentages) || defaultHexColors.percentages;

        // Also initialize RGB inputs for backend compatibility
        this.chartColorInputs.revenue = this.convertToRgb(this.chartColorHexInputs.revenue);
        this.chartColorInputs.donors = this.convertToRgb(this.chartColorHexInputs.donors);
        this.chartColorInputs.subscribers = this.convertToRgb(this.chartColorHexInputs.subscribers);
        this.chartColorInputs.percentages = this.convertToRgb(this.chartColorHexInputs.percentages);
    },
    methods: {
        validateName() {
            if (!this.form.name.trim()) {
                this.errors.name = 'The agency name is required.';
            } else {
                this.errors.name = '';
            }
        },
        validateDomain() {
            if (!this.form.domain || this.form.domain.trim() == '') {
                this.errors.domain = 'The website domain is required.';
            } else {
                // Additional domain format validation can be added here
                //Remove any protocol and trailing slashes
                this.form.domain = this.form.domain.replace(/(^\w+:|^)\/\//, '').replace(/\/$/, '');
                //Ensure it's a valid domain name with optional www prefix
                const regex = /^(?:www\.)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,}$/i;
                if (!regex.test(this.form.domain)) {
                    this.errors.domain = 'The website domain is invalid.';
                    return;
                }
                this.errors.domain = '';
            }
        },
        validateUrlSlug() {
            if (!this.form.url_slug.trim()) {
                this.errors.url_slug = 'The URL is required.';
                return;
            }

            // Regex to allow only lowercase letters, numbers, and hyphens
            const regex = /^[a-z0-9]+(?:-_[a-z0-9]+)*$/;
            if (!regex.test(this.form.url_slug)) {
                this.errors.url_slug = 'The URL slug can only contain lowercase letters, numbers, and hyphens.';
                return;
            }

            // Check uniqueness via API
            this.checkUrlSlugUnique();
        },
        checkUrlSlugUnique() {
            axios.get('/api/agencies/check_url_slug', {
                params: {
                    url_slug: this.form.url_slug,
                    agency_id: this.form.id,
                }
            })
            .then(response => {
                if (!response.data.unique) {
                    this.errors.url_slug = 'The URL slug has already been taken.';
                } else {
                    this.errors.url_slug = '';
                }
            })
            .catch(error => {
                console.error('Error checking URL slug uniqueness:', error);
                this.errors.url_slug = 'Unable to verify URL slug uniqueness.';
            });
        },
        // Billing field validations
        validateCostPerTrial() {
            if (this.form.cost_per_trial === '' || this.form.cost_per_trial === null) {
                this.errors.cost_per_trial = 'Cost per trial is required.';
            } else if (this.form.cost_per_trial < 0) {
                this.errors.cost_per_trial = 'Cost per trial must be a positive number.';
            } else {
                this.errors.cost_per_trial = '';
            }
        },
        validateReferralPercentage() {
            const displayValue = this.referralPercentageDisplay;
            if (displayValue === '' || displayValue === null) {
                this.errors.referral_percentage = 'Referral percentage is required.';
            } else if (parseInt(displayValue) < 0 || parseInt(displayValue) > 100) {
                this.errors.referral_percentage = 'Referral percentage must be between 0 and 100.';
            } else if (!Number.isInteger(parseFloat(displayValue))) {
                this.errors.referral_percentage = 'Referral percentage must be a whole number.';
            } else {
                this.errors.referral_percentage = '';
            }
        },
        validateDiscountType() {
            if (!this.form.cost_per_client_discount_type) {
                this.errors.cost_per_client_discount_type = 'Discount type is required.';
            } else {
                this.errors.cost_per_client_discount_type = '';
            }
        },
        validateDiscountAmount() {
            // If discount type is 'none', no validation needed
            if (this.form.cost_per_client_discount_type === 'none') {
                this.errors.cost_per_client_discount_amount = '';
                return;
            }
            
            if (this.form.cost_per_client_discount_type === 'percentage') {
                const displayValue = this.discountAmountDisplay;
                if (displayValue === '' || displayValue === null) {
                    this.errors.cost_per_client_discount_amount = 'Discount amount is required.';
                } else if (parseInt(displayValue) < 0) {
                    this.errors.cost_per_client_discount_amount = 'Discount amount must be a positive number.';
                } else if (parseInt(displayValue) > 100) {
                    this.errors.cost_per_client_discount_amount = 'Percentage discount must be between 0 and 100.';
                } else if (!Number.isInteger(parseFloat(displayValue))) {
                    this.errors.cost_per_client_discount_amount = 'Percentage discount must be a whole number.';
                } else {
                    this.errors.cost_per_client_discount_amount = '';
                }
            } else {
                if (this.form.cost_per_client_discount_amount === '' || this.form.cost_per_client_discount_amount === null) {
                    this.errors.cost_per_client_discount_amount = 'Discount amount is required.';
                } else if (this.form.cost_per_client_discount_amount < 0) {
                    this.errors.cost_per_client_discount_amount = 'Discount amount must be a positive number.';
                } else {
                    this.errors.cost_per_client_discount_amount = '';
                }
            }
        },
        validatePaymentTerms() {
            if (!this.form.payment_terms) {
                this.errors.payment_terms = 'Payment terms are required.';
            } else {
                this.errors.payment_terms = '';
            }
        },
        validateResaleCertificate() {
            // Boolean field, no validation needed
            this.errors.resale_certificate = '';
        },
        validatePaymentMethod() {
            if (!this.form.payment_method) {
                this.errors.payment_method = 'Payment method is required.';
            } else {
                this.errors.payment_method = '';
            }
        },
        hasError() {
            return this.errors.name != '' || 
                   this.errors.domain != '' || 
                   this.errors.url_slug != '' ||
                   this.errors.cost_per_trial != '' ||
                   this.errors.cost_per_client_discount_type != '' ||
                   this.errors.cost_per_client_discount_amount != '' ||
                   this.errors.referral_percentage != '' ||
                   this.errors.payment_terms != '' ||
                   this.errors.resale_certificate != '' ||
                   this.errors.payment_method != '';
        },
        updateAgency() {
            // Validate all fields before submission
            this.validateName();
            this.validateDomain();
            
            // Validate billing fields if user is admin
            if (this.user && this.user.user_type === 'admin') {
                this.validateCostPerTrial();
                this.validateReferralPercentage();
                this.validateDiscountType();
                this.validateDiscountAmount();
                this.validatePaymentTerms();
                this.validateResaleCertificate();
                this.validatePaymentMethod();
            }

            if (this.hasError()) {
                this.form.error = true;
                return;
            }

            this.form.busy = true;
            this.form.error = false;

            // Prepare form data for API - ensure percentages are in decimal format
            const formData = { ...this.form };
            
            // If there's a new logo, get colors from it before saving
            if (this.form.logo && this.form.logo !== this.agency?.logo) {
                this.getColorsFromImage(this.form.logo, formData);
                return; // Exit early, the actual save will happen in the color extraction callback
            }
            
            // If no new logo, proceed directly with save
            this.performAgencyUpdate(formData);
        },
        updateChartColor(colorType, value) {
            // Update the input value
            this.chartColorInputs[colorType] = this.convertToRgb(value);
            
            // Update form.chart_colors
            if (!this.form.chart_colors) {
                this.form.chart_colors = {};
            }
            
            const rgbValue = this.convertToRgb(value);
            // Only store if different from default
            const defaults = {
                revenue: 'rgb(152 203 46)',
                donors: 'rgb(80 66 255)',
                subscribers: 'rgb(250 174 0)',
                percentages: 'rgb(255 25 81)'
            };
            
            if (rgbValue !== defaults[colorType]) {
                this.form.chart_colors[colorType] = rgbValue;
            } else {
                delete this.form.chart_colors[colorType];
            }
        },
        updateChartColorFromHex(colorType, hexValue) {
            // Ensure hex value has # prefix
            if (!hexValue.startsWith('#')) {
                hexValue = '#' + hexValue;
            }
            
            // Update hex input
            this.chartColorHexInputs[colorType] = hexValue;
            
            // Convert to RGB and update RGB input
            const rgbValue = this.convertToRgb(hexValue);
            this.chartColorInputs[colorType] = rgbValue;
            
            // Update form.chart_colors
            if (!this.form.chart_colors) {
                this.form.chart_colors = {};
            }
            
            // Only store if different from default
            const defaultHex = {
                revenue: '#98CB2E',
                donors: '#5042FF',
                subscribers: '#FAAE00',
                percentages: '#FF1951'
            };
            
            if (hexValue.toUpperCase() !== defaultHex[colorType].toUpperCase()) {
                this.form.chart_colors[colorType] = rgbValue;
            } else {
                delete this.form.chart_colors[colorType];
            }
        },
        resetChartColor(colorType) {
            const defaultHex = {
                revenue: '#98CB2E',
                donors: '#5042FF',
                subscribers: '#FAAE00',
                percentages: '#FF1951'
            };
            
            this.chartColorHexInputs[colorType] = defaultHex[colorType];
            this.updateChartColorFromHex(colorType, defaultHex[colorType]);
        },
        getHexFromColor(color) {
            if (!color) return null;
            
            // If already hex, return it
            if (color.startsWith('#')) {
                return color;
            }
            
            // If RGB, convert to hex
            if (color.startsWith('rgb(')) {
                return this.convertRgbToHex(color);
            }
            
            // Try to parse as hex without #
            if (/^[0-9A-Fa-f]{6}$/.test(color) || /^[0-9A-Fa-f]{3}$/.test(color)) {
                return '#' + color;
            }
            
            return null;
        },
        convertRgbToHex(rgb) {
            if (!rgb || !rgb.startsWith('rgb(')) {
                return null;
            }
            
            // Extract RGB values (handle both "rgb(152 203 46)" and "rgb(152, 203, 46)" formats)
            const match = rgb.match(/rgb\s*\(\s*(\d+)\s*[,\s]\s*(\d+)\s*[,\s]\s*(\d+)\s*\)/i);
            if (!match) {
                return null;
            }
            
            const r = parseInt(match[1]);
            const g = parseInt(match[2]);
            const b = parseInt(match[3]);
            
            // Convert to hex
            const toHex = (n) => {
                const hex = n.toString(16);
                return hex.length === 1 ? '0' + hex : hex;
            };
            
            return '#' + toHex(r) + toHex(g) + toHex(b);
        },
        convertToRgb(color) {
            if (!color || typeof color !== 'string') {
                return color;
            }
            
            // If already in rgb format, return as is
            if (color.trim().startsWith('rgb(')) {
                return color.trim();
            }
            
            // If it's a hex color (with or without #), convert to rgb
            let hex = color.trim();
            if (hex.startsWith('#')) {
                hex = hex.substring(1);
            }
            
            // Check if it's a valid hex color (3 or 6 characters)
            if (/^[0-9A-Fa-f]{3}$/.test(hex)) {
                // Expand 3-digit hex to 6-digit
                hex = hex.split('').map(char => char + char).join('');
            }
            
            if (/^[0-9A-Fa-f]{6}$/.test(hex)) {
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);
                return `rgb(${r} ${g} ${b})`;
            }
            
            // Return as is if format is unknown
            return color;
        },
        performAgencyUpdate(formData) {
            // Convert percentage display values back to decimal for API
            if (this.user && this.user.user_type === 'admin') {
                // Referral percentage is already converted by computed property
                // Discount amount is already converted by computed property
                
                // If discount type is 'none', set discount amount to 0
                if (this.form.cost_per_client_discount_type === 'none') {
                    formData.cost_per_client_discount_amount = 0;
                }
            }

            // Process chart_colors - only include non-default values (convert from hex to RGB for backend)
            const chartColors = {};
            const defaultHex = {
                revenue: '#98CB2E',
                donors: '#5042FF',
                subscribers: '#FAAE00',
                percentages: '#FF1951'
            };
            
            if (this.chartColorHexInputs.revenue && this.chartColorHexInputs.revenue.toUpperCase() !== defaultHex.revenue.toUpperCase()) {
                chartColors.revenue = this.convertToRgb(this.chartColorHexInputs.revenue);
            }
            if (this.chartColorHexInputs.donors && this.chartColorHexInputs.donors.toUpperCase() !== defaultHex.donors.toUpperCase()) {
                chartColors.donors = this.convertToRgb(this.chartColorHexInputs.donors);
            }
            if (this.chartColorHexInputs.subscribers && this.chartColorHexInputs.subscribers.toUpperCase() !== defaultHex.subscribers.toUpperCase()) {
                chartColors.subscribers = this.convertToRgb(this.chartColorHexInputs.subscribers);
            }
            if (this.chartColorHexInputs.percentages && this.chartColorHexInputs.percentages.toUpperCase() !== defaultHex.percentages.toUpperCase()) {
                chartColors.percentages = this.convertToRgb(this.chartColorHexInputs.percentages);
            }
            
            // Only set chart_colors if at least one custom color is set
            if (Object.keys(chartColors).length > 0) {
                formData.chart_colors = chartColors;
            } else {
                formData.chart_colors = null;
            }

            const apiCall = this.form.id != -1
                ? axios.put(`/api/agencies/${this.form.id}`, formData)
                : axios.post('/api/agencies', formData);

            apiCall
            .then(response => {
                this.form.busy = false;
                // If this was a new agency, update the form with the returned ID
                if (!this.form.id && response.data.agency.id) {
                    this.form.id = response.data.agency.id;
                }
                // Emit the updated agency data to parent
                this.$emit('agency-updated', response.data.agency);
                this.showNotification({
                    text: '<strong>' + this.form.name + '</strong> was ' + (this.form.id ? 'updated' : 'created') + ' successfully!'
                });
            })
            .catch(error => {
                this.form.busy = false;
                this.form.error = true;
                
                // Handle validation errors
                if (error.response && error.response.status === 422) {
                    const validationErrors = error.response.data.errors;
                    
                    // Update individual field errors
                    Object.keys(validationErrors).forEach(field => {
                        if (this.errors.hasOwnProperty(field)) {
                            this.errors[field] = validationErrors[field][0];
                        }
                    });
                    
                    // Show validation error toast
                    this.showNotification({
                        text: 'Please correct the validation errors below.',
                        type: 'error'
                    });
                } else {
                    // Show generic error toast
                    this.showNotification({
                        text: 'An error occurred while saving. Please try again.',
                        type: 'error'
                    });
                }
            });
        },
        s3UploadSuccess(s3ObjectLocation){
            //Stupid Amazon URL encoding my folder
            s3ObjectLocation = s3ObjectLocation.replace("%2F", "/");
            s3ObjectLocation = s3ObjectLocation.startsWith("http") ? s3ObjectLocation : 'https:'+ s3ObjectLocation;

            this.form.logo = s3ObjectLocation;
        },
        getColorsFromImage(imageUrl, formData) {
            //Get the primary colors
            window.axios.post('/api/client/get_colors', { logo: imageUrl })
                .then(response => {
                    if(response.data.light_color != null) {
                        formData.light_color = response.data.light_color;
                        formData.dark_color = response.data.dark_color;
                        // Update the form as well
                        this.form.light_color = response.data.light_color;
                        this.form.dark_color = response.data.dark_color;
                    }
                    // Now proceed with the actual save
                    this.performAgencyUpdate(formData);
                })
                .catch(error => {
                    console.error('Error getting colors from image:', error);
                    // Proceed with save even if color extraction fails
                    this.performAgencyUpdate(formData);
                });
        },
        openImageModal() {
            // Open the image edit modal
            const modalElement = document.getElementById('image-edit-modal');
            if (modalElement) {
                const modal = new window.bootstrap.Modal(modalElement);
                modal.show();
            }
        },
        handleImageUpdate(value) {
            if (value === '') {
                // Image was deleted
                this.form.logo = '';
            } else {
                // New image was uploaded - just set the URL without auto-saving
                this.form.logo = value;
            }
        },
        copyRegistrationUrl() {
            const baseUrl = 'https://app.avidai.com/register/';
            const fullUrl = `${baseUrl}${this.form.url_slug}`;

            if (!navigator.clipboard) {
                // Fallback for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = fullUrl;
                textArea.style.position = 'fixed';  // Prevent scrolling to bottom of page in MS Edge.
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                try {
                    document.execCommand('copy');
                    this.showCopySuccess();
                } catch (err) {
                    console.error('Fallback: Oops, unable to copy', err);
                    this.showNotification({
                        text: 'Failed to copy URL. Please try again.',
                        type: 'error'
                    });
                }
                document.body.removeChild(textArea);
                return;
            }

            navigator.clipboard.writeText(fullUrl).then(() => {
                this.showCopySuccess();
            }, (err) => {
                console.error('Async: Could not copy text: ', err);
                this.showNotification({
                    text: 'Failed to copy URL. Please try again manually.',
                    type: 'error'
                });
            });
        },
        showCopySuccess() {
            this.showNotification({
                text: 'The registration URL was copied to your clipboard.'
            });
        }
    },
});
</script>